import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Wheel } from "react-custom-roulette";
import { onAuthStateChanged, signInWithCustomToken } from "firebase/auth";
import { getSpinnerOffersForService, getSpinnerPageConfig } from "../data/spinnerOffers";
import { SPINNER_STRINGS } from "../data/spinnerStrings";
import { auth, firebaseEnvironment } from "../firebase";
import { normalizePhoneNumber, sendSpinnerOtp, verifySpinnerOtp } from "../services/spinnerOtpService";
import { getSavedOfferForService, saveUserOfferForService } from "../services/userOffersService";
import {
  EXISTING_OFFER_TEXT,
  getWheelSize,
  pickMockOffer,
  resolveOfferFromSavedValue,
} from "../utils/spinnerOfferUtils";

const initialOtpState = { phone: "", otp: "", token: "" };
const OTP_RESEND_COOLDOWN_SECONDS = 60;

const createInitialPageState = () => ({
  selectedOffer: null,
  savedOffer: null,
  spinning: false,
  mustSpin: false,
  prizeNumber: 0,
  isRoundComplete: false,
  showCompletionModal: false,
  saveComplete: false,
  error: "",
  otpForm: initialOtpState,
  otpSent: false,
  isVerified: false,
  otpMessage: "",
  isCheckingExistingOffer: false,
  isVerifyingOtp: false,
  isSavingOffer: false,
  resendCooldown: 0,
});

const ServiceOffersSpinner = () => {
  const { serviceSlug } = useParams();
  const pageConfig = useMemo(() => getSpinnerPageConfig(serviceSlug), [serviceSlug]);
  const { actions, completion, spinner } = SPINNER_STRINGS;

  const [offers, setOffers] = useState([]);
  const [pageState, setPageState] = useState(createInitialPageState);
  const [wheelSize, setWheelSize] = useState(() => getWheelSize());
  const resendCooldownTimerRef = useRef(null);
  const autoFilledLookupKeyRef = useRef("");

  const {
    selectedOffer,
    savedOffer,
    spinning,
    mustSpin,
    prizeNumber,
    isRoundComplete,
    showCompletionModal,
    saveComplete,
    error,
    otpForm,
    otpSent,
    isVerified,
    otpMessage,
    isCheckingExistingOffer,
    isVerifyingOtp,
    isSavingOffer,
    resendCooldown,
  } = pageState;

  useEffect(() => {
    setOffers(getSpinnerOffersForService(serviceSlug));
    setPageState(createInitialPageState());
    setWheelSize(getWheelSize());
  }, [serviceSlug]);

  useEffect(() => {
    const handleResize = () => {
      setWheelSize(getWheelSize());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    return () => {
      if (resendCooldownTimerRef.current) {
        window.clearInterval(resendCooldownTimerRef.current);
      }
    };
  }, []);

  const wheelData = useMemo(
    () =>
      offers.map((offer) => ({
        option: offer.title,
      })),
    [offers]
  );

  const startingOptionIndex = useMemo(() => {
    if (!isRoundComplete || prizeNumber < 0) return -1;
    return prizeNumber;
  }, [isRoundComplete, prizeNumber]);

  const updatePageState = useCallback((updates) => {
    setPageState((prev) => ({ ...prev, ...updates }));
  }, []);

  const syncSavedOfferForPhone = useCallback(async (phoneNumber) => {
    try {
      const saved = await getSavedOfferForService({ phoneNumber, serviceSlug });

      if (!saved) return false;

      const resolvedOffer = resolveOfferFromSavedValue({ offers, savedOffer: saved });

      if (!resolvedOffer) {
        updatePageState({
          otpMessage: spinner.mismatchMessage,
          error: spinner.mismatchTitle,
        });
        return true;
      }

      const matchedIndex = Math.max(
        offers.findIndex((offer) => offer.id === resolvedOffer.id),
        0
      );

      updatePageState({
        selectedOffer: resolvedOffer,
        savedOffer: resolvedOffer,
        prizeNumber: matchedIndex,
        mustSpin: false,
        spinning: false,
        isRoundComplete: true,
        isVerified: true,
        otpSent: false,
        showCompletionModal: true,
        saveComplete: false,
        otpMessage: EXISTING_OFFER_TEXT,
      });
      return true;
    } catch (err) {
      console.error("Failed to fetch saved offer", err);
      return false;
    }
  }, [serviceSlug, offers, spinner.mismatchMessage, spinner.mismatchTitle, updatePageState]);

  useEffect(() => {
    if (!offers.length) return;

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      const normalizedPhone = normalizePhoneNumber(currentUser?.phoneNumber || "");
      if (normalizedPhone.length < 10) return;

      const lookupKey = `${serviceSlug}:${normalizedPhone}`;
      if (autoFilledLookupKeyRef.current === lookupKey) return;
      autoFilledLookupKeyRef.current = lookupKey;

      setPageState((prev) => ({
        ...prev,
        otpForm: {
          ...prev.otpForm,
          phone: normalizedPhone,
        },
      }));

      updatePageState({ isCheckingExistingOffer: true, error: "" });
      try {
        await syncSavedOfferForPhone(normalizedPhone);
      } finally {
        updatePageState({ isCheckingExistingOffer: false });
      }
    });

    return () => unsubscribe();
  }, [offers, serviceSlug, syncSavedOfferForPhone, updatePageState]);

  const persistResult = async (winner) => {
    updatePageState({ isSavingOffer: true });
    try {
      await saveUserOfferForService({
        phoneNumber: otpForm.phone,
        serviceSlug,
        offer: winner,
      });

      updatePageState({ savedOffer: winner });
    } catch (err) {
      console.error(err);
      updatePageState({ error: spinner.saveError });
    } finally {
      updatePageState({ isSavingOffer: false });
    }
  };

  const handleOtpFieldChange = (key, value) => {
    setPageState((prev) => ({
      ...prev,
      otpForm: { ...prev.otpForm, [key]: value },
    }));
  };

  const startResendCooldown = () => {
    if (resendCooldownTimerRef.current) {
      window.clearInterval(resendCooldownTimerRef.current);
    }

    updatePageState({ resendCooldown: OTP_RESEND_COOLDOWN_SECONDS });

    resendCooldownTimerRef.current = window.setInterval(() => {
      setPageState((prev) => {
        if (prev.resendCooldown <= 1) {
          if (resendCooldownTimerRef.current) {
            window.clearInterval(resendCooldownTimerRef.current);
            resendCooldownTimerRef.current = null;
          }

          return { ...prev, resendCooldown: 0 };
        }

        return { ...prev, resendCooldown: prev.resendCooldown - 1 };
      });
    }, 1000);
  };

  const handleSendOtp = async () => {
    if (resendCooldown > 0 && otpSent && !isVerified) return;

    updatePageState({ isCheckingExistingOffer: true, error: "" });

    const alreadySaved = await syncSavedOfferForPhone(otpForm.phone);
    if (alreadySaved) {
      updatePageState({ isCheckingExistingOffer: false });
      return;
    }

    const result = await sendSpinnerOtp(otpForm.phone);

    updatePageState({
      otpSent: result.ok,
      otpMessage: result.message,
      error: result.ok ? "" : result.message,
      isCheckingExistingOffer: false,
      otpForm: {
        ...otpForm,
        token: result.token || "",
      },
    });

    if (result.ok) {
      startResendCooldown();
    }
  };

  const handleVerifyOtp = async () => {
    updatePageState({ isVerifyingOtp: true });
    const result = await verifySpinnerOtp({ token: otpForm.token, otp: otpForm.otp });

    if (result.ok && result.firebaseToken && firebaseEnvironment !== "dev") {
      try {
        await signInWithCustomToken(auth, result.firebaseToken);
      } catch (err) {
        console.error("Failed to sign in with custom token", err);
        updatePageState({
          isVerified: false,
          otpMessage: "OTP verified, but failed to sign in. Please try again.",
          isVerifyingOtp: false,
        });
        return;
      }
    }

    if (result.ok && resendCooldownTimerRef.current) {
      window.clearInterval(resendCooldownTimerRef.current);
      resendCooldownTimerRef.current = null;
    }

    updatePageState({
      isVerified: result.ok,
      otpMessage: result.message,
      error: result.ok ? "" : result.message,
      isVerifyingOtp: false,
      resendCooldown: result.ok ? 0 : resendCooldown,
    });
  };

  const resendButtonLabel = otpSent && !isVerified
    ? resendCooldown > 0
      ? `Re${resendCooldown}s`
      : actions.resendOtp
    : actions.sendOtp;

  const isOtpErrorMessage = Boolean(error) && !isVerified;
  const isCountdownVisible = otpSent && !isVerified && resendCooldown > 0;

  const handleSpin = () => {
    if (!isVerified || spinning || mustSpin || !offers.length || isRoundComplete) return;

    updatePageState({ error: "", selectedOffer: null });

    const winner = pickMockOffer(offers);

    if (!winner) {
      updatePageState({ error: spinner.noWinner });
      return;
    }

    const index = Math.max(
      offers.findIndex((offer) => offer.id === winner.id),
      0
    );

    updatePageState({
      prizeNumber: index,
      selectedOffer: winner,
      spinning: true,
      mustSpin: true,
    });
  };

  const onStopSpinning = async () => {
    setTimeout(() => {
      updatePageState({ mustSpin: false, spinning: false, isRoundComplete: true });
    }, 80);

    if (selectedOffer) {
      await persistResult(selectedOffer);
      updatePageState({ showCompletionModal: true, saveComplete: true });
    }
  };

  const isExistingOfferLoaded = isRoundComplete && !saveComplete && otpMessage === EXISTING_OFFER_TEXT;

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#f8fafc] text-slate-900">
      <div className="mx-auto max-w-5xl px-5 py-6 sm:px-6 md:px-6 md:py-10">
        <div className="mb-6 flex flex-col gap-4 border-b border-slate-200 pb-4 sm:flex-row sm:items-start sm:justify-between md:mb-8 md:pb-6">
          <div className="min-w-0 flex items-center gap-3">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full overflow-hidden">
              <img src="/app-logo.png" alt="Local Helper" className="h-full w-full object-cover" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-500">{pageConfig.brandName}</p>
              <h1 className="break-words text-2xl font-semibold tracking-tight capitalize md:text-3xl">{pageConfig.title}</h1>
              <p className="mt-0.5 text-xs text-slate-500">{pageConfig.punjabiLine}</p>
            </div>
          </div>

        </div>

        <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_280px] xl:grid-cols-[minmax(0,1fr)_300px]">
          <section className="min-w-0 rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm md:p-6">
            <div className="mb-5 max-w-2xl md:mb-6">
              <p className="mb-2 text-sm font-medium text-blue-700">{pageConfig.eyebrow}</p>
              <h2 className="text-xl font-semibold tracking-tight text-slate-900 md:text-2xl">
                {pageConfig.mainHeading ?? "Verify your mobile number and reveal your final offer"}
              </h2>
              <p className="mt-2 text-sm leading-6 text-slate-600 md:text-base md:leading-7">
                {pageConfig.subtitle}
              </p>
            </div>

            <div className="mb-4 rounded-[18px] border border-slate-200 bg-slate-50 p-3 md:mb-5 md:p-4">
              <div className="mb-3 flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-slate-500">Step 1</p>
                  <h3 className="text-lg font-semibold text-slate-900">{pageConfig.verificationTitle}</h3>
                  <p className="mt-0.5 text-xs text-slate-500">{pageConfig.verificationSubtitle}</p>
                </div>
                {isVerified ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700 border border-emerald-100">
                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                    Verified
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full bg-slate-200/70 px-3 py-1 text-xs font-medium text-slate-600">
                    Required
                  </span>
                )}
              </div>

              <div className="space-y-2.5">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_116px] sm:items-stretch">
                  <input
                    type="tel"
                    value={otpForm.phone}
                    onChange={(e) => handleOtpFieldChange("phone", e.target.value)}
                    className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-blue-300 placeholder:text-slate-400"
                    placeholder="Mobile number"
                    disabled={isVerified}
                  />

                  <button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={isVerified || isCheckingExistingOffer || (otpSent && !isVerified && resendCooldown > 0)}
                    className={`rounded-xl border border-slate-200 bg-white py-2.5 font-medium leading-none text-slate-900 whitespace-nowrap transition hover:bg-slate-50 disabled:opacity-50 ${isCountdownVisible ? "px-1.5 text-[11px]" : "px-4 text-sm"}`}
                  >
                    {isCheckingExistingOffer ? (
                      <span className="inline-flex items-center justify-center gap-1" aria-label="Sending OTP">
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-700 [animation-delay:-0.2s]" />
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-700 [animation-delay:-0.1s]" />
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-700" />
                      </span>
                    ) : (
                      resendButtonLabel
                    )}
                  </button>
                </div>

                {otpSent && !isVerified && (
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_116px] sm:items-stretch">
                    <input
                      type="text"
                      value={otpForm.otp}
                      onChange={(e) => handleOtpFieldChange("otp", e.target.value)}
                      className="min-w-0 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-blue-300 placeholder:text-slate-400"
                      placeholder="OTP"
                      disabled={isVerified}
                    />

                    <button
                      type="button"
                      onClick={handleVerifyOtp}
                      disabled={isVerified || isCheckingExistingOffer || isVerifyingOtp}
                      className="rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800 disabled:opacity-50"
                    >
                      {isVerifyingOtp ? (
                        <span className="inline-flex items-center justify-center gap-1" aria-label="Verifying OTP">
                          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-white [animation-delay:-0.2s]" />
                          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-white [animation-delay:-0.1s]" />
                          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-white" />
                        </span>
                      ) : (
                        actions.verify
                      )}
                    </button>
                  </div>
                )}
              </div>

              {otpMessage && (
                <p className={`mt-2 text-xs ${isOtpErrorMessage ? "text-red-600" : "text-slate-500"}`}>
                  {otpMessage} ਥੋੜ੍ਹੀ ਦੇਰ ਹੀ ਲੱਗੂ.
                </p>
              )}
            </div>

            {wheelData.length === 0 ? (
              <p className="text-slate-600">{spinner.noOffers}</p>
            ) : (
              <div className="flex flex-col items-center gap-5 md:gap-6">
                <div className={`w-full rounded-[24px] border border-slate-200 bg-slate-50 p-3 md:p-5 transition ${!isVerified ? "opacity-60" : "opacity-100"}`}>
                  <div className="mx-auto flex justify-center">
                    <Wheel
                      size={wheelSize}
                      mustStartSpinning={mustSpin}
                      prizeNumber={prizeNumber}
                      startingOptionIndex={startingOptionIndex}
                      disableInitialAnimation={isRoundComplete}
                      data={wheelData}
                      onStopSpinning={onStopSpinning}
                      backgroundColors={[
                        "#1e3a8a",
                        "#1d4ed8",
                        "#2563eb",
                        "#1e40af",
                        "#3b82f6",
                      ]}
                      textColors={["#ffffff"]}
                      outerBorderColor="#e2e8f0"
                      outerBorderWidth={6}
                      innerBorderColor="#ffffff"
                      innerBorderWidth={14}
                      innerRadius={10}
                      radiusLineColor="#ffffff55"
                      radiusLineWidth={2}
                      fontSize={14}
                      spinDuration={0.8}
                    />
                  </div>
                </div>

                <div className="flex w-full max-w-md flex-col items-center gap-2">
                  <button
                    onClick={handleSpin}
                    disabled={!isVerified || spinning || mustSpin || isRoundComplete}
                    className="w-full rounded-2xl bg-slate-900 px-6 py-3.5 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isSavingOffer
                      ? actions.saveInProgress
                      : savedOffer && isRoundComplete
                      ? actions.alreadyUnlocked
                      : !isVerified
                      ? actions.verifyToContinue
                      : mustSpin
                        ? actions.spinning
                        : isRoundComplete
                          ? actions.revealed
                          : actions.spinToReveal}
                  </button>
                  <p className="text-sm text-slate-500">One reward is issued once the wheel completes. ਇਕੋ ਮੌਕਾ, ਇਕੋ ਆਫਰ.</p>
                </div>

              </div>
            )}
          </section>

          <aside className="min-w-0 space-y-4 lg:sticky lg:top-6 lg:self-start">
            <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm md:p-5">
              <p className="mb-4 text-sm font-medium text-slate-500">Offer details</p>
              <div className="space-y-3 text-sm text-slate-600">
                {pageConfig.offerDetails.map((detail) => (
                  <div key={detail.label} className="flex items-start justify-between gap-4">
                    <span>{detail.label}</span>
                    <span className="font-medium text-slate-900">{detail.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {pageConfig.termsAndConditions?.length > 0 && (
              <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm md:p-5">
                <p className="mb-4 text-sm font-medium text-slate-500">Terms & Conditions</p>
                <div className="space-y-2 text-sm text-slate-600">
                  {pageConfig.termsAndConditions.map((term) => (
                    <p key={term}>{term}</p>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>

      {showCompletionModal && selectedOffer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 px-4">
          <div className="w-full max-w-md rounded-[28px] bg-white p-6 shadow-[0_24px_80px_rgba(15,23,42,0.24)] transition-all duration-500 ease-out">
            <div className="mb-5 flex items-center gap-3 border-b border-slate-100 pb-4">
              <div className={`relative flex h-12 w-12 items-center justify-center rounded-full ${error ? "bg-red-50" : "bg-emerald-50"}`}>
                {!error && <span className="absolute inset-0 rounded-full bg-emerald-100/60 animate-ping" />}
                <span className="relative text-xl">{error ? "⚠️" : "🎉"}</span>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">{error ? "Offer unlocked" : completion.title}</p>
                <h3 className={`text-xl font-semibold ${error ? "text-red-600" : "text-slate-900"}`}>
                  {error ? "Offer save failed" : completion.heading}
                </h3>
              </div>
            </div>

            <div className="rounded-[22px] border border-blue-100 bg-gradient-to-br from-blue-50 to-slate-50 p-5">
              <div className="mt-1 flex items-center justify-center gap-4 text-center">
                <div className="h-px w-8 bg-blue-200" aria-hidden="true" />
                <div>
                  <p className="text-2xl font-semibold text-slate-900">
                    {selectedOffer.percentageOff ? `${selectedOffer.percentageOff}% OFF` : selectedOffer.title}
                  </p>
                  <p className="mt-1 text-sm text-slate-600">{selectedOffer.subtitle}</p>
                </div>
                <div className="h-px w-8 bg-blue-200" aria-hidden="true" />
              </div>
            </div>

            <div className="mt-5 space-y-2 text-sm text-slate-600">
              <p>{error ? "Your offer is shown below, but we could not save it to your number." : completion.journeyComplete}</p>
              {!error && <p>{isExistingOfferLoaded ? completion.existingPunjabi : completion.savedPunjabi}</p>}
              {saveComplete && !error && (
                <p className="text-emerald-600 font-medium">{completion.saveSuccess}</p>
              )}
              {error && <p className="text-red-600 font-medium">{error}</p>}
              {isExistingOfferLoaded && (
                <p className="text-blue-600 font-medium">{completion.existingLoaded}</p>
              )}
            </div>

            <button
              type="button"
              onClick={() => updatePageState({ showCompletionModal: false })}
              className="mt-6 w-full rounded-2xl bg-slate-900 px-4 py-3 text-sm font-medium text-white transition hover:bg-slate-800"
            >
              {completion.done}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceOffersSpinner;